import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Eye, Loader2, LogOut, Search, Trash2, UserPlus, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUSES = ["Yeni", "İncelemede", "Tamamlandı", "Arşiv"];

const STATUS_STYLES = {
  Yeni: "bg-blue-50 text-blue-700 border-blue-200",
  "İncelemede": "bg-amber-50 text-amber-700 border-amber-200",
  "Tamamlandı": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Arşiv": "bg-slate-100 text-slate-500 border-slate-200",
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Tümü");
  const [selected, setSelected] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/submissions");
      setSubmissions(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("kbl_admin_token");
        navigate("/admin");
      } else {
        toast.error("Başvurular yüklenemedi.");
        setSubmissions([]);
      }
    }
  }, [navigate]);

  useEffect(() => {
    document.title = "Kobi-Line | Başvurular";
    load();
  }, [load]);

  const filtered = useMemo(
    () =>
      (submissions || []).filter((s) => {
        const q = search.toLowerCase();
        const matchQ =
          !q ||
          [s.full_name, s.company_name, s.email, s.reference_code].some((v) => v && v.toLowerCase().includes(q));
        const matchS = statusFilter === "Tümü" || s.status === statusFilter;
        return matchQ && matchS;
      }),
    [submissions, search, statusFilter]
  );

  const counts = useMemo(() => {
    const c = { Tümü: (submissions || []).length };
    STATUSES.forEach((st) => {
      c[st] = (submissions || []).filter((s) => s.status === st).length;
    });
    return c;
  }, [submissions]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/submissions/${id}`, { status });
      toast.success(`Durum güncellendi: ${status}`);
      setSelected(null);
      load();
    } catch {
      toast.error("Durum güncellenemedi.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/submissions/${id}`);
      toast.success("Başvuru silindi.");
      load();
    } catch {
      toast.error("Başvuru silinemedi.");
    }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    setNoteSaving(true);
    try {
      await api.post(`/submissions/${selected.id}/notes`, { text: noteText.trim() });
      const { data } = await api.get("/submissions");
      setSubmissions(data);
      setSelected(data.find((s) => s.id === selected.id) || null);
      setNoteText("");
      toast.success("Not eklendi.");
    } catch {
      toast.error("Not eklenemedi.");
    } finally {
      setNoteSaving(false);
    }
  };

  const [usersOpen, setUsersOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "" });
  const [userSaving, setUserSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch {
      toast.error("Kullanıcılar yüklenemedi.");
    }
  }, []);

  const openUsers = () => {
    setUsersOpen(true);
    loadUsers();
  };

  const createUser = async (e) => {
    e.preventDefault();
    setUserSaving(true);
    try {
      await api.post("/users", newUser);
      toast.success(`${newUser.email} eklendi.`);
      setNewUser({ name: "", email: "", password: "" });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kullanıcı eklenemedi.");
    } finally {
      setUserSaving(false);
    }
  };

  const removeUser = async (email) => {
    try {
      await api.delete(`/users/${encodeURIComponent(email)}`);
      toast.success("Kullanıcı silindi.");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kullanıcı silinemedi.");
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Referans", "Tarih", "Ad Soyad", "Firma", "Şirket Türü", "Vergi No", "Kuruluş Yılı", "İl/İlçe", "NACE Kodu", "Telefon", "E-posta", "Sektör", "Çalışan Sayısı", "Durum", "Açıklama"],
      ...filtered.map((s) => [
        s.reference_code, formatDate(s.created_at), s.full_name, s.company_name, s.company_type || "",
        s.tax_number || "", s.foundation_year || "", s.city_district || "", s.nace_code || "",
        s.phone, s.email, s.sector, s.employee_count, s.status, s.description,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kobiline-basvurular-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV dosyası indirildi.");
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    localStorage.removeItem("kbl_admin_token");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-horse.png" alt="Kobi-Line" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-display font-bold text-[#0F172A]">
              KOBİ<span className="text-[#0000FF]">-</span>LİNE <span className="text-[#94A3B8] font-medium text-sm ml-1">Başvurular</span>
            </span>
          </div>
          <button
            onClick={openUsers}
            data-testid="users-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] px-4 py-2 text-sm font-medium text-[#475569] hover:text-[#0000FF] hover:border-[#93C5FD] transition-colors"
          >
            <Users size={15} />
            Kullanıcılar
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] px-4 py-2 text-sm font-medium text-[#475569] hover:text-red-500 hover:border-red-200 transition-colors"
          >
            <LogOut size={15} />
            Çıkış
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {STATUSES.map((st) => (
            <div key={st} className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#94A3B8]">{st}</p>
              <p className="font-display text-3xl font-extrabold text-[#0F172A] mt-1">{counts[st]}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="İsim, firma, e-posta veya referans kodu ara..."
              className="w-full rounded-full border border-[#E2E8F0] bg-white pl-10 pr-4 py-2.5 text-sm outline-none transition-all focus:border-[#0000FF] focus:ring-4 focus:ring-[#0000FF]/10"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
            {["Tümü", ...STATUSES].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                data-testid={`status-filter-${st.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  statusFilter === st
                    ? "bg-[#0F172A] text-white"
                    : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#93C5FD]"
                }`}
              >
                {st}
              </button>
            ))}
          </div>
          <button
            onClick={exportCsv}
            data-testid="export-csv-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#0000FF] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0000D6] transition-colors"
          >
            <Download size={15} />
            CSV İndir
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden" data-testid="admin-submissions-table">
          {submissions === null ? (
            <div className="p-16 flex justify-center">
              <Loader2 size={28} className="animate-spin text-[#0000FF]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-sm text-[#94A3B8]" data-testid="empty-submissions">
              Henüz başvuru bulunmuyor.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left">
                    <th className="px-5 py-4 font-semibold text-[#475569]">Referans</th>
                    <th className="px-5 py-4 font-semibold text-[#475569]">Tarih</th>
                    <th className="px-5 py-4 font-semibold text-[#475569]">Ad Soyad</th>
                    <th className="px-5 py-4 font-semibold text-[#475569]">Firma</th>
                    <th className="px-5 py-4 font-semibold text-[#475569] hidden lg:table-cell">Sektör</th>
                    <th className="px-5 py-4 font-semibold text-[#475569]">Durum</th>
                    <th className="px-5 py-4 font-semibold text-[#475569] text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] transition-colors" data-testid={`submission-row-${s.reference_code}`}>
                      <td className="px-5 py-4 font-mono text-xs font-semibold text-[#0000FF]">{s.reference_code}</td>
                      <td className="px-5 py-4 text-[#475569] whitespace-nowrap">{formatDate(s.created_at)}</td>
                      <td className="px-5 py-4 font-medium text-[#0F172A]">{s.full_name}</td>
                      <td className="px-5 py-4 text-[#475569]">{s.company_name}</td>
                      <td className="px-5 py-4 text-[#475569] hidden lg:table-cell">{s.sector}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-block rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLES[s.status] || ""}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelected(s)}
                            data-testid={`submission-detail-${s.reference_code}`}
                            className="w-8 h-8 rounded-lg grid place-items-center text-[#475569] hover:bg-[#0000FF]/10 hover:text-[#0000FF] transition-colors"
                            title="Detay"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => remove(s.id)}
                            data-testid={`submission-delete-${s.reference_code}`}
                            className="w-8 h-8 rounded-lg grid place-items-center text-[#475569] hover:bg-red-50 hover:text-red-500 transition-colors"
                            title="Sil"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <AnimatePresence>
        {usersOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0B192C]/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setUsersOpen(false)}
            data-testid="users-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="font-display text-xl font-bold text-[#0F172A]">Panel Kullanıcıları</h3>
                  <p className="text-xs text-[#94A3B8] mt-1">Eklediğiniz herkes /admin adresinden panele tam erişimle giriş yapabilir.</p>
                </div>
                <button
                  onClick={() => setUsersOpen(false)}
                  data-testid="close-users-modal"
                  className="w-8 h-8 rounded-lg grid place-items-center text-[#475569] hover:bg-[#F1F5F9] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <ul className="space-y-2 mb-6" data-testid="users-list">
                {users.map((u) => (
                  <li key={u.email} className="flex items-center justify-between gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">{u.name}</p>
                      <p className="text-xs text-[#64748B]">{u.email}</p>
                    </div>
                    <button
                      onClick={() => removeUser(u.email)}
                      data-testid={`user-delete-${u.email.replace(/[^a-zA-Z0-9]/g, "-")}`}
                      className="w-8 h-8 rounded-lg grid place-items-center text-[#475569] hover:bg-red-50 hover:text-red-500 transition-colors"
                      title="Sil"
                    >
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
              <form onSubmit={createUser} className="space-y-3" data-testid="add-user-form">
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))}
                  placeholder="Ad Soyad"
                  required
                  className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-[#0000FF] focus:ring-4 focus:ring-[#0000FF]/10"
                  data-testid="new-user-name"
                />
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))}
                  placeholder="E-posta"
                  required
                  className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-[#0000FF] focus:ring-4 focus:ring-[#0000FF]/10"
                  data-testid="new-user-email"
                />
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))}
                  placeholder="Şifre (en az 8 karakter)"
                  required
                  minLength={8}
                  className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-[#0000FF] focus:ring-4 focus:ring-[#0000FF]/10"
                  data-testid="new-user-password"
                />
                <button
                  type="submit"
                  disabled={userSaving}
                  data-testid="add-user-button"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-5 py-3 hover:bg-[#0000FF] transition-colors disabled:opacity-60"
                >
                  {userSaving ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
                  Kullanıcı Ekle
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#0B192C]/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="submission-detail-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white rounded-3xl p-8 shadow-2xl max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="font-mono text-xs font-semibold text-[#0000FF]">{selected.reference_code}</p>
                  <h3 className="font-display text-xl font-bold text-[#0F172A] mt-1">{selected.company_name}</h3>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{formatDate(selected.created_at)}</p>
                </div>
                <button
                  onClick={() => setSelected(null)}
                  data-testid="close-detail-modal"
                  className="w-8 h-8 rounded-lg grid place-items-center text-[#475569] hover:bg-[#F1F5F9] transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <dl className="space-y-4 text-sm">
                {[
                  ["Ad Soyad", selected.full_name],
                  ["Telefon", selected.phone],
                  ["E-posta", selected.email],
                  ["Şirket Türü", selected.company_type || "—"],
                  ["Vergi Numarası", selected.tax_number || "—"],
                  ["Kuruluş Yılı", selected.foundation_year || "—"],
                  ["İl / İlçe", selected.city_district || "—"],
                  ["NACE Kodu", selected.nace_code || "—"],
                  ["Sektör", selected.sector],
                  ["Çalışan Sayısı", selected.employee_count],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 border-b border-[#F1F5F9] pb-3">
                    <dt className="font-semibold text-[#475569]">{label}</dt>
                    <dd className="text-[#0F172A] text-right">{value}</dd>
                  </div>
                ))}
                <div>
                  <dt className="font-semibold text-[#475569] mb-2">İhtiyaç / Talep</dt>
                  <dd className="text-[#0F172A] leading-relaxed bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] p-4">
                    {selected.description}
                  </dd>
                </div>
              </dl>
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-[#94A3B8] mb-3">Durum Güncelle</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((st) => (
                    <button
                      key={st}
                      onClick={() => updateStatus(selected.id, st)}
                      data-testid={`status-set-${st.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                      className={`rounded-full px-4 py-2 text-xs font-semibold border transition-colors ${
                        selected.status === st
                          ? "bg-[#0F172A] text-white border-[#0F172A]"
                          : "bg-white text-[#475569] border-[#E2E8F0] hover:border-[#93C5FD]"
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>

              {selected.appointment && (
                <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="appointment-info">
                  <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-1">Randevu Talebi</p>
                  <p className="text-sm font-semibold text-[#0F172A]">
                    {new Date(`${selected.appointment.date}T12:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" })} — {selected.appointment.time}
                  </p>
                </div>
              )}

              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-[#94A3B8] mb-3">İç Notlar</p>
                {(selected.notes || []).length > 0 && (
                  <ul className="space-y-2 mb-3" data-testid="notes-list">
                    {(selected.notes || []).map((n, i) => (
                      <li key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-3">
                        <p className="text-sm text-[#0F172A]">{n.text}</p>
                        <p className="text-[11px] text-[#94A3B8] mt-1">{n.by} · {formatDate(n.at)}</p>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addNote()}
                    placeholder="Görüşme notu ekleyin..."
                    className="flex-1 rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-[#0000FF] focus:ring-4 focus:ring-[#0000FF]/10"
                    data-testid="note-input"
                  />
                  <button
                    onClick={addNote}
                    disabled={noteSaving}
                    data-testid="add-note-button"
                    className="rounded-xl bg-[#0F172A] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#0000FF] transition-colors disabled:opacity-60"
                  >
                    {noteSaving ? "..." : "Ekle"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
